import { useEffect, useRef, useState } from 'react';
import type { OrderDetail } from '@/api/types';
import { ApiError } from '@/api/client';
import { Icon } from '@/components/Icon';
import { Tag } from '@/components/Tag';
import { dateTime } from '@/lib/format';
import { useFormFactor } from '@/lib/formFactor';
import { attachOrderFile, uploadOrderAttachment } from './api';
import s from './OrderFilesPanel.module.css';

type Attachment = NonNullable<OrderDetail['attachments']>[number];

function kind(file: Attachment) {
  const name = file.fileName.toLowerCase();
  const type = (file.fileType || '').toLowerCase();
  if (type.startsWith('image/') || /\.(png|jpe?g|webp|gif)$/.test(name)) return { label: '图片', tone: 'blue' as const, icon: 'box', className: s.image };
  if (type.includes('pdf') || name.endsWith('.pdf')) return { label: 'PDF', tone: 'red' as const, icon: 'book', className: s.pdf };
  if (type.includes('spreadsheet') || /\.(xlsx?|csv)$/.test(name)) return { label: 'Excel', tone: 'green' as const, icon: 'dash', className: s.excel };
  return { label: '文件', tone: 'none' as const, icon: 'book', className: s.other };
}

function size(value: number | null | undefined) {
  if (value === null || value === undefined) return '大小未知';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function safeUploadUrl(file: Attachment) {
  try {
    const url = new URL(file.fileUrl, window.location.origin);
    if (url.origin !== window.location.origin || url.search || url.hash || !/^\/uploads\/[^/]+$/.test(url.pathname)) return null;
    const filename = decodeURIComponent(url.pathname.slice('/uploads/'.length));
    if (!filename || filename.includes('/') || filename.includes('\\') || filename === '.' || filename === '..') return null;
    return url.toString();
  } catch { return null; }
}

export function OrderFilesPanel({ files, orderId, canUpload, onUploaded, onUploadReady, externalUpload = false }: { files: Attachment[]; orderId: string; canUpload: boolean; onUploaded: () => void; onUploadReady?: (request: () => void) => void; externalUpload?: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const galleryInput = useRef<HTMLInputElement>(null);
  const ff = useFormFactor();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const [uploadMenuOpen, setUploadMenuOpen] = useState(false);
  useEffect(() => {
    onUploadReady?.(() => ff === 'phone' ? setUploadMenuOpen(true) : input.current?.click());
  }, [ff, onUploadReady]);
  const enabled = canUpload && import.meta.env.VITE_READONLY === '0';
  const upload = async (selected: FileList | File[]) => {
    if (!enabled || busy || uncertain || !selected.length) return;
    setBusy(true); setError('');
    try {
      for (const file of Array.from(selected)) {
        if (file.size < 1 || file.size > 20 * 1024 * 1024) throw new Error(`${file.name} 必须小于 20MB`);
        const stored = await uploadOrderAttachment(file);
        await attachOrderFile(orderId, { fileName: file.name, fileUrl: stored.url, fileType: file.type, fileSize: file.size });
      }
      onUploaded();
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500) setUncertain(true);
      setError(`${cause instanceof Error ? cause.message : '上传失败'}；请刷新订单文件确认结果后再重试。`);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
      if (cameraInput.current) cameraInput.current.value = '';
      if (galleryInput.current) galleryInput.current.value = '';
    }
  };
  const counts = files.reduce((result, file) => {
    const label = kind(file).label;
    result[label] = (result[label] || 0) + 1;
    return result;
  }, {} as Record<string, number>);
  return <div className={`${s.panel} ${externalUpload ? s.externalUpload : ''}`}>
    <div className={s.heading}>
      <div><b>订单文件</b><small>共 {files.length} 个{Object.entries(counts).map(([label, count]) => ` · ${label} ${count}`).join('')}</small></div>
      {!externalUpload && <button type="button" disabled={!enabled || busy || uncertain} onClick={() => ff === 'phone' ? setUploadMenuOpen(true) : input.current?.click()}>{busy ? '上传中…' : '＋ 上传文件'}</button>}
      <input ref={input} type="file" accept="image/*,.pdf,.xlsx,.xls,.csv,.doc,.docx" multiple className={s.fileInput} onChange={(event) => void upload(event.currentTarget.files || [])} />
      <input ref={cameraInput} type="file" accept="image/*" capture="environment" className={s.fileInput} onChange={(event) => void upload(event.currentTarget.files || [])} />
      <input ref={galleryInput} type="file" accept="image/*" multiple className={s.fileInput} onChange={(event) => void upload(event.currentTarget.files || [])} />
    </div>
    {files.length || ff === 'tablet' ? <div className={s.grid}>
      {files.map((file) => {
        const info = kind(file);
        const href = safeUploadUrl(file);
        return <div className={s.card} key={file.id}>
          <div className={`${s.thumb} ${info.className}`}><Icon name={info.icon} size={24} /></div>
          <div className={s.details}>
            {href ? <a className={s.fileName} title={file.fileName} href={href} target="_blank" rel="noreferrer" download={file.fileName}>{file.fileName}</a> : <b className={s.fileName} title={file.fileName}>{file.fileName}</b>}
            <div className={s.meta}><Tag tone={info.tone} sm>{info.label}</Tag><small>{size(file.fileSize)}</small></div>
            {!!file.linkedShipmentNos?.length && <span title={`关联发货批次：${file.linkedShipmentNos.join('、')}`}><Tag tone="green" sm>已关联发货</Tag></span>}
            {!file.linkedShipmentNos?.length && <small className={s.date}>{dateTime(file.createdAt)}{file.usageNote ? ` · ${file.usageNote}` : ''}</small>}
            {!href && <span className={s.unavailable}>文件地址不可用</span>}
          </div>
        </div>;
      })}
      {ff === 'tablet' && <div className={s.drop} aria-disabled={!enabled || busy || uncertain} onDragOver={(event) => { if (enabled) event.preventDefault(); }} onDrop={(event) => { if (!enabled) return; event.preventDefault(); void upload(event.dataTransfer.files); }} onClick={() => { if (enabled && !busy && !uncertain) input.current?.click(); }}><Icon name="up" size={24} /><span>拖入或点此上传</span><small>单文件不超过 20MB</small></div>}
    </div> : <p className={s.empty}>这张订单还没有文件。</p>}
    {error && <p className={s.error} role="alert">{error}</p>}
    <p className={s.hint}>文件存在服务器私有目录，下载时会反查引用关系并校验当前账号是否有权查看这张订单。</p>
    {uploadMenuOpen && <div className={s.uploadBackdrop} onMouseDown={(event) => { if (event.target === event.currentTarget) setUploadMenuOpen(false); }}>
      <section className={s.uploadSheet} role="dialog" aria-modal="true" aria-label="上传文件">
        <header><b>上传文件</b><small>图片、PDF、Excel，单个不超过 20 MB</small></header>
        <button type="button" onClick={() => { setUploadMenuOpen(false); cameraInput.current?.click(); }}>拍照</button>
        <button type="button" onClick={() => { setUploadMenuOpen(false); galleryInput.current?.click(); }}>从相册选择</button>
        <button type="button" onClick={() => { setUploadMenuOpen(false); input.current?.click(); }}>从文件选择</button>
        <button type="button" className={s.uploadCancel} onClick={() => setUploadMenuOpen(false)}>取消</button>
      </section>
    </div>}
  </div>;
}
